/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { datadogRum } from "@datadog/browser-rum"
import { Middleware, UnknownAction } from "@reduxjs/toolkit"
import { isFetchLifecycleEnabled } from "constants/datadog"
import { EventActionEng, EventCategoryEng } from "constants/tracking"
import { AppState } from "store/store.type"
import { TroyWebTracker } from "utils/tracking"

export const FETCH_ATTEMPT_ACTION = "fetch_attempt"
export const FETCH_STATE_UPDATED_ACTION = "fetch_state_updated"
export const BUTTON_TRIGGERED_ACTION = "button_triggered"

export interface ButtonTriggeredRumContext {
  trigger: string
  action_type?: string
  endpoint_name?: string
}

interface RequestPayload {
  request?: {
    url?: string
  }
}

interface ActionMeta {
  requestId?: string
  aborted?: boolean
  condition?: boolean
  previousAction?: {
    type?: string
    payload?: RequestPayload
  }
  arg?: {
    endpointName?: string
  }
}

type LifecycleAction = UnknownAction & {
  payload?: RequestPayload
  meta?: ActionMeta
}

const rtkPendingStarts = new Map<string, number>()
const legacyPendingStarts = new WeakMap<object, number>()

const normalizeUrl = (url?: string): string => url?.split(/[?#]/)[0] ?? ""

const linkPage = (): string =>
  window?.location?.pathname?.split(/[?#]/)[0] ?? ""

const reportRumAction = (
  name: string,
  context: Record<string, string | undefined>,
): void => {
  if (!isFetchLifecycleEnabled()) {
    return
  }

  try {
    datadogRum.addAction(name, { ...context, link_page: linkPage() })
  } catch {
    // Datadog may be unavailable outside instrumented sessions.
  }
}

export const reportButtonTriggeredRumAction = (
  context: ButtonTriggeredRumContext,
): void => {
  reportRumAction(BUTTON_TRIGGERED_ACTION, {
    trigger: context.trigger,
    action_type: context.action_type,
    endpoint_name: context.endpoint_name,
  })
}

const isRtkPending = (type: string): boolean =>
  type.endsWith("/executeQuery/pending") ||
  type.endsWith("/executeMutation/pending") ||
  type.endsWith("/executeInfiniteQuery/pending")

const isRtkTerminal = (type: string): boolean =>
  type.endsWith("/executeQuery/fulfilled") ||
  type.endsWith("/executeQuery/rejected") ||
  type.endsWith("/executeMutation/fulfilled") ||
  type.endsWith("/executeMutation/rejected") ||
  type.endsWith("/executeInfiniteQuery/fulfilled") ||
  type.endsWith("/executeInfiniteQuery/rejected")

const recordRtkPendingStart = (requestId?: string): void => {
  if (requestId) {
    rtkPendingStarts.set(requestId, Date.now())
  }
}

const recordLegacyPendingStart = (action: LifecycleAction): void => {
  legacyPendingStarts.set(action, Date.now())
}

const getRtkDurationMs = (requestId?: string): number | undefined => {
  if (!requestId) {
    return undefined
  }

  const start = rtkPendingStarts.get(requestId)
  if (start === undefined) {
    return undefined
  }

  rtkPendingStarts.delete(requestId)
  return Date.now() - start
}

const getLegacyDurationMs = (previousAction?: object): number | undefined => {
  if (!previousAction) {
    return undefined
  }

  const start = legacyPendingStarts.get(previousAction)
  if (start === undefined) {
    return undefined
  }

  legacyPendingStarts.delete(previousAction)
  return Date.now() - start
}

const discardRtkPendingStart = (requestId?: string): void => {
  if (requestId) {
    rtkPendingStarts.delete(requestId)
  }
}

const reportFetchLifecycleScribe = ({
  architecture_type,
  duration_ms,
  status,
  action_type,
  endpoint_name,
  request_id,
}: {
  architecture_type: "old_redux" | "rtk_query"
  duration_ms: number
  status: "success" | "error"
  action_type?: string
  endpoint_name?: string
  request_id?: string
}): void => {
  if (!isFetchLifecycleEnabled()) {
    return
  }

  try {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchLifecycleLoadTime,
      labels: {
        ARCHITECTURE_TYPE: architecture_type,
        DURATION_MS: duration_ms,
        STATUS: status,
        LINK_PAGE: linkPage(),
        FROM_RTK_QUERY: architecture_type === "rtk_query",
        FROM_OLD_REDUX: architecture_type === "old_redux",
        ACTION_NAME: endpoint_name ?? action_type ?? "",
        ENDPOINT_NAME: endpoint_name,
        REQUEST_ID: request_id,
      },
    })
  } catch {
    // Scribe may be unavailable outside instrumented sessions.
  }
}

const reportLegacyStateUpdated = (action: LifecycleAction): void => {
  const actionType = action.type as string
  const previousAction = action.meta?.previousAction
  if (!previousAction?.type) {
    return
  }

  const previousType = previousAction.type as string
  const isSuccess = actionType === `${previousType}_SUCCESS`
  const isError =
    actionType === `${previousType}_FAIL` ||
    actionType === `${previousType}_FAILED`

  if (!isSuccess && !isError) {
    return
  }

  const endpointUrl = normalizeUrl(previousAction.payload?.request?.url)
  const durationMs = getLegacyDurationMs(previousAction)
  if (durationMs === undefined) {
    return
  }

  const status = isSuccess ? "success" : "error"

  reportRumAction(FETCH_STATE_UPDATED_ACTION, {
    architecture_type: "old_redux",
    action_type: previousType,
    endpoint_url: endpointUrl,
    status,
  })

  reportFetchLifecycleScribe({
    architecture_type: "old_redux",
    action_type: previousType,
    status,
    duration_ms: durationMs,
  })
}

const reportRtkStateUpdated = (action: LifecycleAction): void => {
  const actionType = action.type as string
  if (!isRtkTerminal(actionType)) {
    return
  }

  const requestId = action.meta?.requestId
  if (action.meta?.aborted || action.meta?.condition) {
    discardRtkPendingStart(requestId)
    return
  }

  const endpointName = action.meta?.arg?.endpointName
  const status = actionType.endsWith("/fulfilled") ? "success" : "error"
  const durationMs = getRtkDurationMs(requestId)

  reportRumAction(FETCH_STATE_UPDATED_ACTION, {
    architecture_type: "rtk_query",
    endpoint_name: endpointName,
    status,
  })

  if (durationMs !== undefined) {
    reportFetchLifecycleScribe({
      architecture_type: "rtk_query",
      endpoint_name: endpointName,
      request_id: requestId,
      status,
      duration_ms: durationMs,
    })
  }
}

/** Before axios — catches outgoing legacy requests and RTK pending actions. */
export const fetchLifecycleAttemptMiddleware: Middleware<
  {},
  AppState
> = () => next => (action: UnknownAction) => {
  const lifecycleAction = action as LifecycleAction
  const actionType = lifecycleAction.type as string
  const requestUrl = lifecycleAction.payload?.request?.url

  if (requestUrl) {
    recordLegacyPendingStart(lifecycleAction)
    reportRumAction(FETCH_ATTEMPT_ACTION, {
      architecture_type: "old_redux",
      action_type: actionType,
      endpoint_url: normalizeUrl(requestUrl),
    })
  }

  if (isRtkPending(actionType)) {
    recordRtkPendingStart(lifecycleAction.meta?.requestId)
    reportRumAction(FETCH_ATTEMPT_ACTION, {
      architecture_type: "rtk_query",
      endpoint_name: lifecycleAction.meta?.arg?.endpointName,
    })
  }

  return next(action)
}

/**
 * After axios — legacy SUCCESS/FAIL are dispatched via axios middleware's
 * `next()`, so they never re-enter middleware placed before axios.
 */
export const fetchLifecycleCompleteMiddleware: Middleware<
  {},
  AppState
> = () => next => (action: UnknownAction) => {
  const lifecycleAction = action as LifecycleAction
  const result = next(action)

  reportLegacyStateUpdated(lifecycleAction)
  reportRtkStateUpdated(lifecycleAction)

  return result
}
